import { useState } from 'react'
import { Share2, Link2, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { track } from '@/lib/track'
import { cn } from '@/lib/utils'

type ShareButtonsProps = {
  eventId?: string
  title: string
  url: string
  label?: string
  triggerClassName?: string
}

export default function ShareButtons({
  eventId,
  title,
  url,
  label = 'Share Event',
  triggerClassName = 'w-full',
}: ShareButtonsProps) {
  const [copied, setCopied] = useState(false)

  function handleShare(platform: string) {
    if (eventId) {
      track(eventId, 'share_click', { platform })
    }

    const text = encodeURIComponent(title)
    const encodedUrl = encodeURIComponent(url)

    switch (platform) {
      case 'x':
        window.open(`https://x.com/intent/tweet?text=${text}&url=${encodedUrl}`, '_blank')
        break
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank')
        break
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, '_blank')
        break
      case 'email':
        window.location.href = `mailto:?subject=${text}&body=${encodedUrl}`
        break
      case 'copy':
        navigator.clipboard.writeText(url).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
        })
        break
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={cn(triggerClassName)}>
          <Share2 className="mr-2 size-4" />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => handleShare('copy')}>
          {copied ? <Check className="mr-2 size-4" /> : <Link2 className="mr-2 size-4" />}
          {copied ? 'Copied!' : 'Copy Link'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('x')}>
          Share on X
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('facebook')}>
          Share on Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('linkedin')}>
          Share on LinkedIn
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('email')}>
          Share via Email
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
